import { WalletManager } from './wallet.js';
import { ContractManager } from './contract.js';
import { UIManager } from './ui.js';
import { SoundManager } from './sound.js';
import { TransactionManager } from './transactions.js';
import { MusicManager } from './music.js';
import { I18nManager } from './i18n.js';
import { debugWallet } from './wallet-debug.js';

class App {
    constructor() {
        this.walletManager = new WalletManager();
        this.contractManager = new ContractManager();
        this.i18nManager = new I18nManager();
        this.uiManager = new UIManager(this.i18nManager);
        this.soundManager = new SoundManager();
        this.transactionManager = new TransactionManager();
        this.musicManager = new MusicManager();
        
        this.init();
    }

    async init() {
        debugWallet();
        this.setupEventListeners();
        await this.checkWalletConnection();
        this.startDataPolling();
        this.musicManager.init();
        this.i18nManager.init();
        this.setupLanguageSwitcher();
    }

    setupEventListeners() {
        const connectBtn = document.getElementById('connectWalletBtn');
        const disconnectBtn = document.getElementById('disconnectBtn');
        const activateBtn = document.getElementById('activateBtn');
        const closeErrorBtn = document.getElementById('closeErrorBtn');
        const logoRefresh = document.getElementById('logoRefresh');

        connectBtn?.addEventListener('click', () => this.connectWallet());
        disconnectBtn?.addEventListener('click', () => this.disconnectWallet());
        activateBtn?.addEventListener('click', () => this.activateBitcoin());
        closeErrorBtn?.addEventListener('click', () => this.uiManager.hideError());
        
        // Logo click to refresh page
        logoRefresh?.addEventListener('click', () => {
            console.log('🔄 Оновлення сторінки через логотип...');
            window.location.reload();
        });
    }

    setupLanguageSwitcher() {
        const langEn = document.getElementById('langEn');
        const langUa = document.getElementById('langUa');

        langEn?.addEventListener('click', () => {
            this.switchLanguage('en');
        });

        langUa?.addEventListener('click', () => {
            this.switchLanguage('ua');
        });

        // Set active state based on current language
        this.updateLanguageButtons();
    }

    switchLanguage(lang) {
        this.i18nManager.setLang(lang);
        this.updateLanguageButtons();
    }

    updateLanguageButtons() {
        const currentLang = this.i18nManager.getCurrentLang();
        const langEn = document.getElementById('langEn');
        const langUa = document.getElementById('langUa');

        langEn?.classList.toggle('active', currentLang === 'en');
        langUa?.classList.toggle('active', currentLang === 'ua');
    }

    async checkWalletConnection() {
        const isConnected = await this.walletManager.checkConnection();
        if (isConnected) {
            const address = this.walletManager.getAddress();
            this.onWalletConnected(address);
        }
    }

    async connectWallet() {
        try {
            console.log('Спроба підключення гаманця...');
            const address = await this.walletManager.connect();
            console.log('Гаманець підключено:', address);
            this.soundManager.playConnect();
            this.onWalletConnected(address);
        } catch (error) {
            console.error('Помилка підключення гаманця:', error);
            this.soundManager.playError();
            
            let errorMsg = this.i18nManager.t('wallet_connection_failed');
            if (typeof window.Bitcoin === 'undefined' && typeof window.opnet === 'undefined') {
                errorMsg += this.i18nManager.t('wallet_not_detected');
            } else {
                errorMsg += this.i18nManager.t('try_again_reload');
            }
            
            this.uiManager.showError(errorMsg);
        }
    }

    disconnectWallet() {
        console.log('🔌 Відключення гаманця...');
        
        // Відключаємо гаманець
        this.walletManager.disconnect();
        
        // Очищуємо дані транзакцій
        this.transactionManager.clearTransactions();
        
        // Оновлюємо UI
        this.uiManager.showConnectSection();
        this.uiManager.hideWalletInfo();
        
        // Показуємо повідомлення про відключення
        const message = this.i18nManager.t('wallet_disconnected');
        this.uiManager.showActivationFeedback({ success: true, message });
        
        console.log('✅ Гаманець відключено');
    }

    onWalletConnected(address) {
        this.uiManager.showWalletInfo(address);
        this.uiManager.showActivationSection();
        this.loadUserStats(address);
        this.loadGlobalStats();
        this.loadLeaderboard();
        this.loadUserTransactions(address);
    }

    loadUserTransactions(address) {
        const transactions = this.transactionManager.loadTransactions(address);
        this.uiManager.updateTransactions(transactions);
    }

    async activateBitcoin() {
        const activateBtn = document.getElementById('activateBtn');
        if (!activateBtn || activateBtn.disabled) return;

        try {
            activateBtn.disabled = true;
            activateBtn.style.opacity = '0.6';
            activateBtn.textContent = 'Signing transaction...';

            const address = this.walletManager.getAddress();
            if (!address) {
                throw new Error('Wallet not connected');
            }

            console.log('🚀 Початок активації Bitcoin...');

            const result = await this.contractManager.activate(
                address, 
                this.walletManager.provider
            );
            
            console.log('✅ Активація успішна!', result);
            
            this.soundManager.playActivate();
            this.uiManager.showActivationFeedback(result);
            
            await this.loadUserStats(address);
            await this.loadGlobalStats();
            await this.loadLeaderboard();
            this.loadUserTransactions(address);

        } catch (error) {
            console.error('❌ Помилка активації:', error);
            this.soundManager.playError();
            
            let errorMsg = 'Activation failed. ';
            const errMessage = String(error?.message || '');
            const errCode = error?.code;

            if (
                errCode === 4001 ||
                errMessage.includes('User rejected') ||
                errMessage.includes('rejected the request') ||
                errMessage.includes('code: 4001')
            ) {
                errorMsg += 'You rejected the transaction signature request.';
            } else if (errMessage.includes('Wallet not connected')) {
                errorMsg += 'Wallet not connected.';
            } else if (errMessage.includes('Funding transaction not created')) {
                errorMsg += 'The wallet did not create a funding transaction in time. Please confirm the wallet prompt faster and try again.';
            } else {
                errorMsg += errMessage || 'Please try again.';
            }
            
            this.uiManager.showError(errorMsg);
        } finally {
            activateBtn.disabled = false;
            activateBtn.style.opacity = '1';
            activateBtn.innerHTML = '<img src="/images/flame_icon.png" alt="Flame" class="flame-icon"><span>ACTIVATE MY BITCOIN</span><img src="/images/flame_icon.png" alt="Flame" class="flame-icon">';
        }
    }

    async loadUserStats(address) {
        try {
            const stats = await this.contractManager.getUserStats(address);
            this.uiManager.updateUserStats(stats);
        } catch (error) {
            console.error('Failed to load user stats:', error);
        }
    }

    async loadGlobalStats() {
        try {
            const stats = await this.contractManager.getGlobalStats();
            this.uiManager.updateGlobalStats(stats);
        } catch (error) {
            console.error('Failed to load global stats:', error);
        }
    }

    async loadLeaderboard() {
        try {
            const leaderboard = await this.contractManager.getLeaderboard();
            this.uiManager.updateLeaderboard(leaderboard);
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
        }
    }

    startDataPolling() {
        setInterval(() => {
            const address = this.walletManager.getAddress();
            if (address) {
                this.loadUserStats(address);
                this.loadGlobalStats();
                this.loadLeaderboard();
            }
        }, 10000);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new App();
});
